// frontend/src/components/SampleVideo.jsx

import React, { useState, useEffect, useRef } from 'react';
import { showSuccess } from '../utils/toastUtils';
import 'react-toastify/dist/ReactToastify.css';
import { CheckIcon, EyeIcon } from '@heroicons/react/outline';
import api from '../services/api';
import { getTranslation } from '../i18n/translations';
import { useLanguage } from '../contexts/LanguageContext';

const S3_BASE = 'https://pc-assessmentreports.s3.ap-south-1.amazonaws.com/assessment_files';
const SampleVideo = ({
    accessToken,
    onComplete,
    videoUrl,
    isWatched = false,
    assessmentId: assessmentIdProp
}) => {
    const [language, setLanguageLocal] = useState('en');
    const { setLanguage } = useLanguage();
    const [loading, setLoading] = useState(false);
    const [watched, setWatched] = useState(isWatched);
    const [videoError, setVideoError] = useState(false);
    const [assessmentId, setAssessmentId] = useState(assessmentIdProp || null);
    const [sampleVideoName, setSampleVideoName] = useState(null);
    const videoRef = useRef(null);
    const t = (key, replacements) => getTranslation(language, key, replacements);

    // ============================================
    // FETCH SESSION
    // ============================================
    useEffect(() => {
        const fetchSession = async () => {
            try {
                const res = await api.get(`/sessions/${accessToken}/status`);
                const session = res.data.session;

                const lang = session?.language || 'en';
                setLanguageLocal(lang);
                setLanguage(lang);

                if (session?.assessment_id && !assessmentId) {
                    setAssessmentId(session.assessment_id);
                }

                if (session?.sample_video_name) {
                    setSampleVideoName(session.sample_video_name);
                }
            } catch (err) {
                console.error('Error fetching session:', err);
            }
        };
        fetchSession();
    }, [accessToken]);

    // ============================================
    // BUILD THE ACTUAL VIDEO URL — S3 preferred
    // ============================================
    const resolvedVideoUrl = (() => {
        if (sampleVideoName && assessmentId) {
            return `${S3_BASE}/${assessmentId}/videos/${sampleVideoName}`;
        }
        return videoUrl;
    })();

    // ============================================
    // INSTRUCTIONS
    // ============================================
    const getInstructions = () => {
        const lang = language || 'en';

        const instructionsMap = {
            'EBCSS-00003': {
                en: {
                    title: 'Instructions',
                    subtitle: 'Watch this sample video to understand the assessment format',
                    description: 'This assessment contains video-based questions with multiple choice options. Watch the video carefully and select the best response.'
                },
                hi: {
                    title: 'निर्देश',
                    subtitle: 'मूल्यांकन प्रारूप को समझने के लिए इस नमूना वीडियो को देखें',
                    description: 'इस मूल्यांकन में बहुविकल्पीय विकल्पों के साथ वीडियो-आधारित प्रश्न शामिल हैं। वीडियो को ध्यान से देखें और सर्वोत्तम उत्तर चुनें।'
                }
            },
            'EBCSS-00004': {
                kn: {
                    title: 'ಸೂಚನೆಗಳು',
                    subtitle: 'ಮೌಲ್ಯಮಾಪನ ಸ್ವರೂಪವನ್ನು ಅರ್ಥಮಾಡಿಕೊಳ್ಳಲು ಈ ಮಾದರಿ ವೀಡಿಯೊವನ್ನು ವೀಕ್ಷಿಸಿ',
                    description: 'ಈ ಮೌಲ್ಯಮಾಪನವು ವೀಡಿಯೊ ಆಧಾರಿತ ಪ್ರಶ್ನೆಗಳನ್ನು ಒಳಗೊಂಡಿದೆ. ವೀಡಿಯೊವನ್ನು ಎಚ್ಚರಿಕೆಯಿಂದ ವೀಕ್ಷಿಸಿ ಮತ್ತು ಉತ್ತಮ ಪ್ರತಿಕ್ರಿಯೆಯನ್ನು ಆಯ್ಕೆಮಾಡಿ.'
                },
                en: {
                    title: 'Instructions',
                    subtitle: 'Watch this sample video to understand the assessment format',
                    description: 'This assessment contains video-based questions. Watch the video carefully and select the best response.'
                }
            }
        };

        const assessInstr = instructionsMap[assessmentId] || {};
        const langInstr = assessInstr[lang] || assessInstr['en'];

        if (langInstr) return langInstr;

        return {
            title: t('video.title'),
            subtitle: t('video.subtitle'),
            description: t('video.description')
        };
    };

    const inst = getInstructions();

    // ============================================
    // CONVERT DRIVE URL → EMBED
    // ============================================
    const getEmbedUrl = (url) => {
        if (!url) return null;
        if (url.includes('/preview')) return url;

        if (url.includes('drive.google.com')) {
            let fileId = null;
            const m1 = url.match(/\/d\/([^/]+)/);
            if (m1 && m1[1]) fileId = m1[1];
            if (!fileId) {
                const m2 = url.match(/id=([^&]+)/);
                if (m2 && m2[1]) fileId = m2[1];
            }
            if (fileId) return `https://drive.google.com/file/d/${fileId}/preview`;
        }
        return url;
    };

    const embedUrl = getEmbedUrl(videoUrl);

    // ============================================
    // MARK WATCHED
    // ============================================
    const handleWatchComplete = async () => {
        if (watched) return;
        try {
            setLoading(true);
            const response = await api.post(`/sessions/${accessToken}/sample-video-watched`);
            if (response.data.success) {
                setWatched(true);
            }
        } catch (error) {
            console.error('Error marking video as watched:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleContinue = () => {
        if (!watched) handleWatchComplete();
        onComplete?.();
    };

    const handleVideoError = () => setVideoError(true);

    // ============================================
    // NO VIDEO FALLBACK
    // ============================================
    if (!videoUrl) {
        return (
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 text-center">
                <p className="text-gray-600 mb-4">{t('video.no_video')}</p>
                <button
                    onClick={handleContinue}
                    className="px-6 py-2 bg-[#ff9f2f] text-white rounded-lg hover:bg-[#e68a1a]"
                >
                    {t('video.continue')}
                </button>
            </div>
        );
    }

    // ============================================
    // LAYOUT — CARD WITH VIDEO ON TOP, INSTRUCTIONS BELOW
    // ============================================
    return (
        <div className="w-full max-w-4xl mx-auto">
            {/* Header with title */}
            <div className="bg-white rounded-t-2xl shadow-sm border border-gray-200 border-b-0 px-6 py-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-[#063b56] flex items-center justify-center">
                            <EyeIcon className="h-5 w-5 text-white" />
                        </div>
                        <div>
                            <h1 className="text-lg font-bold text-[#063b56]">
                                {inst.title}
                            </h1>
                            <p className="text-xs text-gray-500">
                                {inst.subtitle}
                            </p>
                        </div>
                    </div>
                    {/* {assessmentId && (
                        <span className="text-xs bg-gray-100 text-gray-600 px-3 py-1 rounded-full font-medium">
                            {assessmentId}
                        </span>
                    )} */}
                </div>
            </div>

            {/* Body */}
            <div className="bg-white rounded-b-2xl shadow-sm border border-gray-200 p-6">

                {/* Video player — 16:9 box */}
                <div className="w-full bg-black rounded-lg overflow-hidden aspect-video mb-4">
                    {!videoError && resolvedVideoUrl ? (
                        sampleVideoName ? (
                            /* ⭐ S3 → native <video> with fullscreen allowed */
                            <video
                                src={resolvedVideoUrl}
                                className="w-full h-full"
                                controls
                                autoPlay
                                playsInline
                                controlsList="nodownload noremoteplayback"
                                onContextMenu={(e) => e.preventDefault()}
                                onEnded={handleWatchComplete}
                                onError={() => setVideoError(true)}
                            >
                                {t('video.no_support') || 'Your browser does not support the video tag.'}
                            </video>
                        ) : (
                            /* Drive → iframe */
                            <iframe
                                src={resolvedVideoUrl}
                                className="w-full h-full"
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                                title="Sample Video"
                                onError={() => setVideoError(true)}
                            />
                        )
                    ) : (
                        <div className="w-full h-64 flex items-center justify-center bg-gray-800">
                            <p className="text-white text-sm">{t('video.drive_error')}</p>
                        </div>
                    )}
                </div>

                {/* Description */}
                <div className="mt-5 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-sm text-blue-700 leading-relaxed">
                        {inst.description}
                    </p>
                </div>

                {/* Status + Continue */}
                <div className="mt-6 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                    <div>
                        {watched ? (
                            <div className="flex items-center text-green-600">
                                <CheckIcon className="h-5 w-5 mr-2" />
                                <span className="font-medium text-sm">
                                    {t('video.watched') || 'Video watched!'}
                                </span>
                            </div>
                        ) : (
                            <div className="flex items-center text-gray-500">
                                <EyeIcon className="h-5 w-5 mr-2" />
                                <span className="text-sm">
                                    {t('video.watch_prompt') || 'Watch the video to continue'}
                                </span>
                            </div>
                        )}
                    </div>

                    <button
                        onClick={handleContinue}
                        disabled={loading}
                        className="px-6 py-2.5 bg-[#ff9f2f] text-white rounded-lg hover:bg-[#e68a1a] transition-all flex items-center justify-center shadow-sm disabled:opacity-50 font-medium"
                    >
                        <CheckIcon className="h-4 w-4 mr-2" />
                        {loading
                            ? (t('common.loading') || 'Loading...')
                            : (t('video.continue') || 'Continue to Assessment')
                        }
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SampleVideo;